import {
  AppLayout,
  LoadingPage,
  Media,
  NavigationBlocker,
  PageSidebar,
} from "shared/components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ButtonProps, Text, useToastContext } from "@ttc3k/trekker";
import { Flex } from "styled-system/jsx";
import { useEffect, useState } from "react";
import {
  EnumEntityReferenceType,
  EnumOwnerType,
  PublicationStatusType,
  useOperatorByIdQuery,
} from "gql/generated";
import {
  formatPhoneNumber,
  getAddressString,
  getMultilingualString,
  getTagPropsFromPublicationStatus,
} from "shared/utils";
import {
  ApproveOperatorModal,
  OperatorTabs,
} from "apps/directory/Operator/components";
import { OperatorTabsEnum } from "apps/directory/shared";
import {
  Activity,
  BackOffice,
  Connections,
  // FeaturedIn,
  Profile,
  Staff,
} from "apps/directory/Operator/views";
import { useApproveOperator } from "apps/directory/Operator/hooks";
import { css } from "styled-system/css";
import { useLoggedInAs } from "shared/hooks";
import { useUpdateProfileForm } from "apps/directory/Operator/views/Profile/useUpdateProfileForm.tsx";
import { OperatorContextProvider } from "apps/directory/Operator/OperatorContext.tsx";
import { useAppContext } from "AppContext";
import { scale } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { getOperatorTabsConfig } from "./components/OperatorTabs/getOperatorTabsConfig";
import { OperatorEvents } from "./views/OperatorEvents";

export const Operator = () => {
  const { t } = useTranslation();
  const { setIsNavigationBlocked } = useAppContext();
  const { toastFactory } = useToastContext();
  const { operatorEntityId } = useParams();
  const loggedInAs = useLoggedInAs();
  const { data, loading, error } = useOperatorByIdQuery({
    variables: { operatorId: operatorEntityId ?? "" },
  });

  const operatorTabsConfig = getOperatorTabsConfig(t, loggedInAs);
  const {
    isApproveModalOpen,
    onApproveOperatorClick,
    onApproveOperatorClickInModal,
    onCloseApprovalModal,
    isApproveOpModalDisabled,
  } = useApproveOperator(operatorEntityId, data?.operatorById._versionId);

  const [tab, setTab] = useState<OperatorTabsEnum>(
    operatorTabsConfig[0].value as OperatorTabsEnum,
  );

  const [primaryButtonProps, setPrimaryButtonProps] =
    useState<ButtonProps | null>(null);

  const [saveButtonProps, setSaveButtonProps] = useState<ButtonProps | null>(
    null,
  );

  const [isApprovingOperator, setIsApprovingOperator] = useState(false);

  const handleTabChange = (value: OperatorTabsEnum) => {
    setSaveButtonProps(null);
    setTab(value);
  };

  const { form, onSubmit } = useUpdateProfileForm({
    operator: data?.operatorById,
    onSuccess: (operatorName) => {
      form.reset();
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("directory:OPERATOR.PROFILE.FORM_SUCCESS", {
          name: operatorName,
        }),
      });
      setIsApprovingOperator(false);
    },
    onError: (formError) => {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: formError,
      });
    },
    isApprovingOperator,
  });

  const getCurrentOperatorView = (tab: OperatorTabsEnum) => {
    switch (tab) {
      case OperatorTabsEnum.BackOffice:
        return <BackOffice />;
      case OperatorTabsEnum.Profile:
        return (
          <Profile
            form={form}
            formId={"update-profile-form"}
            onSubmit={onSubmit}
          />
        );
      case OperatorTabsEnum.Media:
        return (
          <Media
            images={data?.operatorById.images ?? []}
            setSaveButtonProps={setSaveButtonProps}
            setPrimaryButtonProps={setPrimaryButtonProps}
            entityType={EnumEntityReferenceType.Operator}
            _entityId={operatorEntityId ?? ""}
          />
        );
      case OperatorTabsEnum.Staff:
        return <Staff />;
      // case OperatorTabsEnum.FeaturedIn:
      //   return <FeaturedIn />;
      case OperatorTabsEnum.Events:
        return <OperatorEvents />;
      case OperatorTabsEnum.Activity:
        return <Activity />;
      case OperatorTabsEnum.Connections:
        return <Connections />;
    }
  };

  useEffect(() => {
    setIsNavigationBlocked(
      form.formState.isDirty ||
        ((saveButtonProps && !saveButtonProps.disabled) ?? false),
    );
  }, [form.formState.isDirty, saveButtonProps, setIsNavigationBlocked]);

  /**
   * If there is a # value in the URL, treat it as the desired first tab
   * to load. We'll switch to that tab AND reset the URL to remove the #
   */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const hash = (location.hash ?? "").substring(1).toUpperCase();
    if (Object.values(OperatorTabsEnum).includes(hash as OperatorTabsEnum)) {
      navigate(location.pathname, { replace: true });
      handleTabChange(hash as OperatorTabsEnum);
    }
  }, [location.hash, location.pathname, navigate]);

  if (loading) return <LoadingPage />;

  if (error && !loading) {
    throw new Error(error.message);
  }

  if (!data || !data.operatorById) {
    throw new Error("Cannot find operator");
  }

  const titleString = getMultilingualString(data.operatorById.name);
  const tagProps = getTagPropsFromPublicationStatus(
    data.operatorById.status as PublicationStatusType,
  );

  return (
    <OperatorContextProvider operator={data.operatorById}>
      <AppLayout
        sidebar={
          <PageSidebar
            title={titleString}
            tagProps={tagProps}
            imageProps={{
              image: data.operatorById.images[0],
              resize: scale().height(260),
            }}
            primaryButtonProps={{
              visual: "success",
              children: t(
                `directory:OPERATOR.SIDEBAR.CTA_BUTTON_COPY.${form.formState.isDirty ? "SAVE_AND_APPROVE" : "APPROVE_PROFILE"}`,
              ),
              disabled:
                data.operatorById.status === PublicationStatusType.Approved,
              className: css({
                display:
                  loggedInAs?.type === EnumOwnerType.Organization &&
                  data.operatorById.status !== PublicationStatusType.Approved
                    ? "block"
                    : "none",
              }),
              form: "update-profile-form",
              type:
                isApproveOpModalDisabled && form.formState.isDirty
                  ? "submit"
                  : "button",
              onClick: () => {
                if (form.formState.isDirty) {
                  setIsApprovingOperator(true);
                  if (!isApproveOpModalDisabled) onApproveOperatorClick();
                } else {
                  onApproveOperatorClick();
                }
              },
              ...(primaryButtonProps && primaryButtonProps),
            }}
            secondaryButtonProps={
              tab === OperatorTabsEnum.Profile
                ? {
                    children: t("core:SAVE_CHANGES"),
                    disabled: !form.formState.isDirty,
                    form: "update-profile-form",
                    type: "submit",
                  }
                : saveButtonProps && {
                    children: t("core:SAVE_CHANGES"),
                    ...saveButtonProps,
                  }
            }
          >
            <Flex flexDir={"column"} gap={"100"}>
              {data.operatorById.contact.address && (
                <Text visual={"bodyMedium"} color={"text.mid"}>
                  {getAddressString(data.operatorById.contact.address)}
                </Text>
              )}
              {data.operatorById.contact.phone && (
                <Text visual={"bodyMedium"} color={"text.mid"}>
                  {formatPhoneNumber(data.operatorById.contact.phone)}
                </Text>
              )}
              {data.operatorById.contact.email && (
                <Text visual={"bodyMedium"} color={"text.mid"}>
                  {data.operatorById.contact.email}
                </Text>
              )}
            </Flex>
          </PageSidebar>
        }
        content={
          <Flex direction={"column"} h={"fit-content"} w={"full"} gap={"300"}>
            <NavigationBlocker>
              <OperatorTabs currentTab={tab} onTabChange={handleTabChange} />
            </NavigationBlocker>
            {getCurrentOperatorView(tab)}
          </Flex>
        }
      />
      <ApproveOperatorModal
        isOpen={isApproveModalOpen}
        onClose={onCloseApprovalModal}
        onApproveOperatorClick={onApproveOperatorClickInModal}
        form={form}
        formId={"update-profile-form"}
        onSubmit={onSubmit}
      />
    </OperatorContextProvider>
  );
};
